import { FeedingPlanType } from '../common'
import { getPouchesPerDayFromDailyServingGrams, getPouchSizeFromDailyServingGrams } from './dailyServingPouches'
import { LYKA_HALF_BOWL_FEEDING_PLAN_TYPES, TOPPER_DAILY_SERVING_GRAMS } from './utils'

/**
 * Pouch allocation for the meal-plan snapshot, faithful to the "How pouches are allocated" spec,
 * computed entirely from data the order already carries at fulfilment time:
 *
 *   - `fullBowlDailyServingGrams` — the dog's full-bowl daily serving in grams, snapshotted onto the
 *     order at creation (`order_animal_servings.daily_serving_size_in_grams`). Point-in-time, so it
 *     reflects what shipped, not the (mutable) live subscription.
 *   - `planType` — the per-order plan type (`order_line_items.plan_type`), which carries the bowl
 *     arrangement including starter boxes.
 *
 * Pouch size and pouches-per-day are derived from the full-bowl grams via the shared
 * `DAILY_SERVING_POUCHES_PER_DAY` map. That map is validated against every `FeedingPlanServingSize`
 * slug prefix in the tests, so grams alone unambiguously reconstruct the pouch combination — no
 * serving-size slug is required. (The spec's warning that "the system does not divide every serving
 * by one standard pouch size" is exactly what the map encodes: combination sizes ship 2/3/4 pouches.)
 *
 * Half-bowl plans (`LYKA_HALF_BOWL_FEEDING_PLAN_TYPES`, incl. the halving starter boxes) keep the
 * same physical pouch size but halve the amount fed each day — the spec's 0.5 "plan factor". That is
 * why a half-bowl dog can be served "½ × 300g pouch/day": the pouch stays 300g, only the count halves.
 */

/** Whether a plan type ships half the full-bowl amount (the 0.5 plan factor). */
export function isHalfBowlPlanType(planType: FeedingPlanType): boolean {
  return LYKA_HALF_BOWL_FEEDING_PLAN_TYPES.has(planType)
}

export interface MealPlanPouchAllocation {
  /** Physical pouch size in grams (same for full and half bowl). */
  pouchSizeGrams: number
  /** Pouches per day for a full-bowl day, before any half-bowl factor. A Topper's day is its one pouch. */
  fullBowlPouchesPerDay: number
  /** Pouches actually fed per day: full-bowl pouches/day × plan factor (0.5 for half bowl). */
  pouchesPerDay: number
  /** Grams actually fed per day: full-bowl serving × plan factor (0.5 for half bowl), or 125g on a Topper. */
  dailyServingGrams: number
  /** Topper only: the share of the Full-Bowl Serving its 125g covers, as a whole percent. */
  shareOfDailyNeedsPercent?: number
}

type LykaMealPlanType = Exclude<FeedingPlanType, typeof FeedingPlanType.tyga_full_bowl | typeof FeedingPlanType.tyga_mixed_bowl>
/** A fraction of the Full-Bowl Serving, or the Topper's fixed 125g a day. */
type MealPlanSizing = 1 | 0.5 | 'topper'

/**
 * How much of the Full-Bowl Serving each Lyka plan type feeds. Exhaustive so a new plan type fails to
 * compile until it is sized here; a value outside it (e.g. an unrecognised `plan_type`) throws rather
 * than falling back to Full Bowl. The Topper is a fixed 125g a day, not a fraction.
 */
const MEAL_PLAN_SIZING: Record<LykaMealPlanType, MealPlanSizing> = {
  [FeedingPlanType.full_bowl]: 1,
  [FeedingPlanType.starter_box]: 1,
  [FeedingPlanType.trial_box]: 1,
  [FeedingPlanType.starter_box_7_days]: 1,
  [FeedingPlanType.starter_box_14_days]: 1,
  [FeedingPlanType.starter_box_1_week]: 1,
  [FeedingPlanType.starter_box_half_1_week]: 1,
  [FeedingPlanType.starter_box_quarter_2_week]: 1,
  [FeedingPlanType.trial_box_7x200g]: 1,
  [FeedingPlanType.trial_box_14x200g]: 1,
  [FeedingPlanType.half_bowl]: 0.5,
  [FeedingPlanType.starter_box_half]: 0.5,
  [FeedingPlanType.starter_box_full_1w_over_2w]: 0.5,
  [FeedingPlanType.topper]: 'topper',
  [FeedingPlanType.starter_box_topper]: 'topper',
}

/**
 * Resolve the meal-plan pouch allocation from the full-bowl daily serving (grams) and plan type.
 *
 * Invariant: `dailyServingGrams === pouchSizeGrams × pouchesPerDay`.
 *
 * Examples from the spec:
 *   (300,  full_bowl)        → 300g pouch, 1 × 300g/day,  300g/day
 *   (300,  starter_box_half) → 300g pouch, ½ × 300g/day,  150g/day
 *   (1200, half_bowl)        → 600g pouch, 1 × 600g/day,  600g/day
 *   (1800, half_bowl)        → 600g pouch, 1½ × 600g/day, 900g/day
 */
export function getMealPlanPouchAllocation(
  fullBowlDailyServingGrams: number,
  planType: FeedingPlanType,
  enable700gDoublePouches = false,
): MealPlanPouchAllocation {
  if (!Number.isFinite(fullBowlDailyServingGrams) || fullBowlDailyServingGrams <= 0) {
    throw new Error(`Daily serving size is invalid ${fullBowlDailyServingGrams}`)
  }

  const sizing = MEAL_PLAN_SIZING[planType as LykaMealPlanType] as MealPlanSizing | undefined
  if (sizing === undefined) {
    throw new Error(`Unsupported plan type for meal plan pouch allocation: ${planType}`)
  }

  if (sizing === 'topper') {
    return {
      pouchSizeGrams: TOPPER_DAILY_SERVING_GRAMS,
      fullBowlPouchesPerDay: 1,
      pouchesPerDay: 1,
      dailyServingGrams: TOPPER_DAILY_SERVING_GRAMS,
      shareOfDailyNeedsPercent: Math.round((TOPPER_DAILY_SERVING_GRAMS / fullBowlDailyServingGrams) * 100),
    }
  }

  const fullBowlPouchesPerDay = getPouchesPerDayFromDailyServingGrams(fullBowlDailyServingGrams, enable700gDoublePouches)
  const pouchSizeGrams = getPouchSizeFromDailyServingGrams(fullBowlDailyServingGrams, enable700gDoublePouches)

  return {
    pouchSizeGrams,
    fullBowlPouchesPerDay,
    pouchesPerDay: fullBowlPouchesPerDay * sizing,
    dailyServingGrams: fullBowlDailyServingGrams * sizing,
  }
}
